<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue'
import { useElementHover } from '@vueuse/core'
import { Button } from '@/components/ui/button'
import { useDiversityResultsStore } from '@/stores/diversityResultsStore'
import { useBiosecurityResultsStore } from '@/stores/biosecurityResultsStore'
import { useValidationResultsStore } from '@/stores/validationResultsStore'

interface Props {
  modelValue: [number, number]
  method: string
  column?: string
  palette: string
  label: string
  removable?: boolean
  isActive?: boolean
}

interface Emits {
  (e: 'update:modelValue', value: [number, number]): void
  (e: 'remove'): void
  (e: 'update:isActive', value: boolean): void
  (e: 'update:palette', value: string): void
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => [0, 1],
  palette: 'divergent',
  label: 'Color Range',
  removable: true,
  isActive: true
})

const emit = defineEmits<Emits>()

// Access stores for data availability checking
const diversityResultsStore = useDiversityResultsStore()
const biosecurityResultsStore = useBiosecurityResultsStore()
const validationResultsStore = useValidationResultsStore()

// Local slider state
const sliderRange = ref<[number, number]>([...props.modelValue])
const sliderRef = ref<HTMLElement | null>(null)
const tooltipRef = ref<HTMLElement | null>(null)

// Interaction state
const isDragging = ref(false)
const activeHandle = ref<number | 'range' | null>(null)
const rangeDragStart = ref<{ mouseX: number; startPos: number; endPos: number } | null>(null)

// Prevent recursive updates
const isUpdatingFromProps = ref(false)

// Tooltip state
const tooltipVisible = ref(false)
const tooltipPosition = ref({ x: 0, y: 0 })

// Palette button tooltip state
const paletteButtonTooltipVisible = ref(false)
const paletteButtonTooltipPosition = ref({ x: 0, y: 0 })

// Hover detection
const isHovered = useElementHover(sliderRef)

// Active state
const toggleActive = () => { emit('update:isActive', !props.isActive) }

// Color palettes for gradient circles (from DivergentSlider.vue)
const colorPalettes = {
  sequential_centered: ['#9E0142', '#D53E4F', '#F46D43', '#FDAE61', '#FEE08B', '#E6F598', '#ABDDA4', '#66C2A5', '#3288BD', '#5E4FA2'],
  sequential: ['#440154', '#414487', '#2A788E', '#22A884', '#7AD151', '#FDE725'],
  divergent: ['#002BFF', '#0066FF', '#0099FF', '#00CCFF', '#00FFFF', '#66FFCC', '#99FF99', '#CCFF66', '#FFFF00', '#FFCC00', '#FF9900', '#FF2B00'],
  qualitative: ['#A6CEE3', '#1F78B4', '#B2DF8A', '#33A02C', '#FB9A99', '#E31A1C', '#FDBF6F', '#FF7F00', '#CAB2D6', '#6A3D9A', '#FFFF99', '#B15928'],
  rag: ['#22C55E', '#F59E0B','#EF4444'] // Green for good, amber for caution, red for critical
}

// Palette options for tooltip
const paletteOptions = [
  { key: 'qualitative', label: 'Qual.', description: 'Qualitative is best for categorical data when order is not important' },
  { key: 'divergent', label: 'Div.', description: 'Divergent is useful to highlight deviations above and below the midpoint' },
  { key: 'sequential', label: 'Seq.', description: 'Used for data with a clear, ordered progression from low to high' },
  { key: 'sequential_centered', label: 'Seq.C', description: 'Sequential Centered is for a range of values with a center' },
  { key: 'rag', label: 'RAG', description: 'RAG (Red Amber Green) status for 3-level validation' }
]

// Check data availability for different colormap methods
const hasValidationData = computed(() => {
  return validationResultsStore.getValidationResults.size > 0
})

const hasDiversityData = computed(() => {
  return diversityResultsStore.getGlobalMetrics.totalPlants > 0
})

const hasBiosecurityData = computed(() => {
  return biosecurityResultsStore.getGlobalMetrics.totalSpecies > 0
})

// Determine applicable palettes based on method and available data
const applicablePalettes = computed(() => {
  const method = props.method
  
  // Different methods support different palettes based on their data requirements
  switch (method) {
    case 'diversity':
      if (!hasDiversityData.value) return []
      // Diversity supports RAG (for threshold violations) and sequential (for abundance)
      return ['rag', 'sequential']
      
    case 'names':
      if (!hasValidationData.value) return []
      // Names validation primarily uses RAG for match status
      return ['rag']
      
    case 'family':
      if (!hasValidationData.value) return []
      // Family uses qualitative for categorical distinction
      return ['qualitative']
      
    case 'column':
      // Column data typically uses divergent/sequential for numeric data
      return ['divergent', 'sequential', 'sequential_centered']
      
    case 'taxa':
      if (!hasValidationData.value) return []
      // Taxa uses qualitative for categorical distinction
      return ['qualitative']
      
    case 'biosecurity':
      if (!hasBiosecurityData.value) return []
      // Biosecurity supports RAG (for risk levels), sequential (for pest counts), and divergent (for risk scores)
      return ['rag', 'sequential', 'divergent']
      
    case 'availability':
    case 'distribution':
      // These methods would typically use divergent/sequential for scoring/percentage data
      // They don't depend on validation/diversity stores, so always available if method is selected
      return ['divergent', 'sequential', 'rag']
      
    default:
      // For unknown/generic methods, fall back to all options
      return paletteOptions.map(p => p.key)
  }
})

// Get gradient style for palette option
const getPaletteGradient = (paletteKey: string) => {
  const colors = colorPalettes[paletteKey as keyof typeof colorPalettes] || colorPalettes.divergent
  return `linear-gradient(to right, ${colors.slice(0, 5).join(', ')})`
}

// Current gradient colors based on palette
const currentGradientColors = computed(() => {
  return colorPalettes[props.palette as keyof typeof colorPalettes] || colorPalettes.divergent
})

// Watch for prop changes
watch(() => props.modelValue, (newValue) => {
  if (!isDragging.value && !isUpdatingFromProps.value) {
    isUpdatingFromProps.value = true
    sliderRange.value = [...newValue]
    // Clear flag after next tick to allow internal updates
    setTimeout(() => {
      isUpdatingFromProps.value = false
    }, 0)
  }
}, { deep: true })

// Handle palette change
const changePalette = (newPalette: string) => {
  emit('update:palette', newPalette)
}

// Watch for changes in applicable palettes and auto-switch if current palette becomes invalid
watch(applicablePalettes, (newApplicable, oldApplicable) => {
  // Only act if applicable palettes actually changed
  if (JSON.stringify(newApplicable) === JSON.stringify(oldApplicable)) return
  
  const currentPalette = props.palette
  
  // If current palette is no longer applicable, switch to first applicable
  if (newApplicable.length > 0 && !newApplicable.includes(currentPalette)) {
    console.log(`[DivergentSlider] Auto-switching from invalid palette "${currentPalette}" to "${newApplicable[0]}" for method "${props.method}"`)
    changePalette(newApplicable[0])
  } else if (newApplicable.length === 0) {
    console.warn(`[DivergentSlider] No applicable palettes for method "${props.method}" - data not available`)
  }
}, { immediate: true })

// Emit changes - always emit when dragging to ensure handle movement works
watch(sliderRange, (newValue) => {
  if (!isUpdatingFromProps.value || isDragging.value) {
    if (isDragging.value) {
      console.log(`[DivergentSlider] Emitting range update during drag for ${props.method}:`, newValue)
    }
    emit('update:modelValue', [...newValue])
  }
}, { deep: true })

// Tooltip content
const tooltipContent = computed(() => {
  const [start, end] = sliderRange.value
  const startLabel = `${Math.round(start * 100)}%`
  const endLabel = `${Math.round(end * 100)}%`
  
  return `Range: ${startLabel} → ${endLabel}`
})

// Current palette description for tooltip
const currentPaletteDescription = computed(() => {
  const applicable = applicablePalettes.value
  const currentDesc = paletteOptions.find(p => p.key === props.palette)?.description || ''
  
  if (applicable.length === 0) {
    return `No applicable palettes for ${props.method} - no data available`
  } else if (applicable.length === 1) {
    return `${currentDesc} (only applicable palette for ${props.method})`
  } else {
    return `${currentDesc} (${applicable.length} applicable palettes for ${props.method})`
  }
})



// Cycle through palettes - only through applicable ones
const cyclePalette = () => {
  const applicable = applicablePalettes.value
  
  // If no applicable palettes, don't cycle
  if (applicable.length === 0) {
    console.warn(`[DivergentSlider] No applicable palettes for method "${props.method}" - no data available`)
    return
  }
  
  // If only one applicable palette, don't cycle
  if (applicable.length === 1) {
    console.log(`[DivergentSlider] Only one applicable palette for method "${props.method}": ${applicable[0]}`)
    changePalette(applicable[0])
    return
  }
  
  // Find current palette in applicable list
  const currentIndex = applicable.findIndex(key => key === props.palette)
  
  if (currentIndex === -1) {
    // Current palette not in applicable list, switch to first applicable
    console.log(`[DivergentSlider] Current palette "${props.palette}" not applicable for method "${props.method}", switching to ${applicable[0]}`)
    changePalette(applicable[0])
  } else {
    // Cycle to next applicable palette
    const nextIndex = (currentIndex + 1) % applicable.length
    const nextPalette = applicable[nextIndex]
    console.log(`[DivergentSlider] Cycling from ${props.palette} to ${nextPalette} for method "${props.method}"`)
  changePalette(nextPalette)
  }
}

// Palette button tooltip handlers
const showPaletteButtonTooltip = (event: MouseEvent) => {
  const rect = (event.target as HTMLElement).getBoundingClientRect()
  paletteButtonTooltipPosition.value = {
    x: rect.left + rect.width / 2,
    y: rect.top - 10
  }
  paletteButtonTooltipVisible.value = true
}

const hidePaletteButtonTooltip = () => {
  paletteButtonTooltipVisible.value = false
}



// Mouse interaction handlers
function getClosestHandle(position: number): number {
  const [start, end] = sliderRange.value
  const distanceToStart = Math.abs(position - start)
  const distanceToEnd = Math.abs(position - end)
  return distanceToStart <= distanceToEnd ? 0 : 1
}

function isInMiddleArea(position: number): boolean {
  const [start, end] = sliderRange.value
  const handleWidth = 0.06 // Reduced from 0.12 to make middle area larger
  
  return position > start + handleWidth && 
         position < end - handleWidth &&
         (end - start) > handleWidth * 3 && // Reduced from 6 to 3 for easier middle dragging
         Math.abs(position - (start + end) / 2) < (end - start) / 2 // Increased from /4 to /2 for larger middle area
}

function handleMouseDown(event: MouseEvent) {
  if (!sliderRef.value) return
  
  const rect = sliderRef.value.getBoundingClientRect()
  const x = event.clientX - rect.left
  const position = Math.max(0, Math.min(1, x / rect.width))
  
  const [start, end] = sliderRange.value
  const handleSensitivity = 0.04 // Reduced from 0.08 to make handles less aggressive
  const distanceToStart = Math.abs(position - start)
  const distanceToEnd = Math.abs(position - end)
  
  // Prioritize middle area dragging over handle detection
  if (isInMiddleArea(position)) {
    activeHandle.value = 'range'
    rangeDragStart.value = {
      mouseX: event.clientX,
      startPos: sliderRange.value[0],
      endPos: sliderRange.value[1]
    }
  } else if (distanceToStart <= handleSensitivity || distanceToEnd <= handleSensitivity) {
    activeHandle.value = getClosestHandle(position)
  } else {
    activeHandle.value = getClosestHandle(position)
  }
  
  isDragging.value = true
  updatePosition(event)
  updateTooltipPosition(event)
  document.addEventListener('mousemove', handleMouseMove)
  document.addEventListener('mouseup', handleMouseUp)
}

function handleMouseMove(event: MouseEvent) {
  if (!isDragging.value || !sliderRef.value) return
  
  // Use requestAnimationFrame for smoother updates
  requestAnimationFrame(() => {
    updatePosition(event)
    updateTooltipPosition(event)
  })
}

function handleMouseUp() {
  isDragging.value = false
  activeHandle.value = null
  rangeDragStart.value = null
  tooltipVisible.value = false
  document.removeEventListener('mousemove', handleMouseMove)
  document.removeEventListener('mouseup', handleMouseUp)
}

function updatePosition(event: MouseEvent) {
  if (!sliderRef.value || activeHandle.value === null) return
  
  const rect = sliderRef.value.getBoundingClientRect()
  const x = event.clientX - rect.left
  const position = Math.max(0, Math.min(1, x / rect.width))
  
  let newRange: [number, number] | null = null
  
  if (activeHandle.value === 'range' && rangeDragStart.value) {
    const deltaX = event.clientX - rangeDragStart.value.mouseX
    const deltaPosition = deltaX / rect.width
    const rangeWidth = rangeDragStart.value.endPos - rangeDragStart.value.startPos
    
    let newStart = rangeDragStart.value.startPos + deltaPosition
    let newEnd = rangeDragStart.value.endPos + deltaPosition
    
    if (newStart < 0) {
      newStart = 0
      newEnd = rangeWidth
    }
    if (newEnd > 1) {
      newEnd = 1
      newStart = 1 - rangeWidth
    }
    
    newRange = [newStart, newEnd]
  } else if (typeof activeHandle.value === 'number') {
    newRange = [...sliderRange.value] as [number, number]
    newRange[activeHandle.value] = position
    
    if (activeHandle.value === 0 && position >= newRange[1]) {
      newRange[0] = Math.max(0, newRange[1] - 0.01)
    } else if (activeHandle.value === 1 && position <= newRange[0]) {
      newRange[1] = Math.min(1, newRange[0] + 0.01)
    }
  }
  
  // Update if there's any change - removed micro-update threshold for better responsiveness
  if (newRange) {
    sliderRange.value = newRange
  }
}

function updateTooltipPosition(event: MouseEvent) {
  tooltipPosition.value = {
    x: event.clientX,
    y: event.clientY - 40
  }
  tooltipVisible.value = true
}



function handleMouseMoveOnSlider(event: MouseEvent) {
  if (!isDragging.value) {
    updateTooltipPosition(event)
    if (isHovered.value) {
      tooltipVisible.value = true
    }
  }
}

function handleMouseLeave() {
  if (!isDragging.value) {
    tooltipVisible.value = false
  }
}

// Gradient and styling - cached for performance
const sliderGradient = computed(() => {
  const colors = currentGradientColors.value
  
  // Create stepped gradient with hard color stops
  const steps = colors.length
  const stepSize = 100 / steps
  
  const colorStops = colors.map((color, index) => {
    const start = index * stepSize
    const end = (index + 1) * stepSize
    return `${color} ${start}%, ${color} ${end}%`
  }).join(', ')
  
  return `linear-gradient(to right, ${colorStops})`
})

const rangeSelectionStyle = computed(() => {
  const [start, end] = sliderRange.value
  const leftPercentage = start * 100
  const rightPercentage = end * 100
  const widthPercentage = rightPercentage - leftPercentage
  
  const handleMargin = 1
  
  return {
    left: `calc(${leftPercentage}% + ${handleMargin}px)`,
    width: `calc(${widthPercentage}% - ${handleMargin * 2}px)`
  }
})

const handlePositions = computed(() => {
  const [start, end] = sliderRange.value
  const handleRadius = 9
  
  return {
    start: `calc(${handleRadius}px + ${start} * (100% - ${handleRadius * 2}px))`,
    end: `calc(${handleRadius}px + ${end} * (100% - ${handleRadius * 2}px))`
  }
})

// Cleanup
onUnmounted(() => {
  document.removeEventListener('mousemove', handleMouseMove)
  document.removeEventListener('mouseup', handleMouseUp)
})
</script>

<template>
  <div 
    class="group w-full rounded-lg p-3 hover:bg-background transition-all duration-150"
    :class="[!props.isActive ? 'opacity-40 grayscale' : '', { 'transition-all duration-150': !isDragging }]"
  >
        <!-- Header with label, eye toggle, and remove button -->
    <div class="flex items-center justify-between mb-3 ">
      <span class="text-sm font-medium text-foreground select-none">{{ label }}</span>
      <div class="flex items-center gap-2 opacity-0 group-hover:opacity-100">
        <Button
          variant="ghost"
          size="sm"
          @click="toggleActive"
          class="h-fit w-fit p-1 rounded-full hover:bg-primary hover:text-background"
        >
          <Icon :name="props.isActive ? 'lucide:eye' : 'lucide:eye-off'" class="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          @click="cyclePalette"
          @mouseenter="showPaletteButtonTooltip"
          @mouseleave="hidePaletteButtonTooltip"
          :disabled="applicablePalettes.length <= 1"
          class="h-fit w-fit p-1 rounded-full relative"
          :class="applicablePalettes.length <= 1 
            ? 'opacity-50 cursor-not-allowed' 
            : 'hover:bg-primary hover:text-background'"
        >
          <Icon name="lucide:palette" class="w-4 h-4" />
          <!-- Badge showing number of applicable palettes -->
          <span 
            v-if="applicablePalettes.length > 1"
            class="absolute -top-1 -right-1 bg-primary text-background text-xs rounded-full w-4 h-4 flex items-center justify-center font-medium"
          >
            {{ applicablePalettes.length }}
          </span>
        </Button>
        <Button
          v-if="removable"
          variant="ghost"
          size="sm"
          @click="emit('remove')"
          class="h-fit w-fit p-1 hover:bg-primary rounded-full hover:text-background"
        >
          <Icon name="lucide:x" class="w-4 h-4" />
        </Button>
      </div>
    </div>

    <!-- Slider -->
    <div class="relative">
      <div 
        ref="sliderRef"
        class="relative w-full h-[18px] rounded-full border border-border shadow-inner cursor-pointer"
        :style="{ background: sliderGradient }"
        @mousedown="handleMouseDown"
        @mousemove="handleMouseMoveOnSlider"
        @mouseleave="handleMouseLeave"
      >
        <!-- Outer gradient dimming overlays -->
        <div 
          class="absolute top-0 h-full bg-popover opacity-80 dark:opacity-95 dark:mix-blend-multiply rounded-l-full pointer-events-none"
          :style="{ 
            left: '0%', 
            width: `${sliderRange[0] * 100}%` 
          }"
        ></div>
        
        <div 
          class="absolute top-0 h-full bg-popover opacity-80 dark:opacity-95 dark:mix-blend-multiply rounded-r-full pointer-events-none"
          :style="{ 
            left: `${sliderRange[1] * 100}%`, 
            width: `${(1 - sliderRange[1]) * 100}%` 
          }"
        ></div>
        
        <!-- Start Handle -->
        <div 
          class="absolute top-1/2 w-[18px] h-[18px] bg-popover border-2 border-primary/80 rounded-full transform -translate-x-1/2 -translate-y-1/2 cursor-grab shadow-md hover:shadow-lg active:cursor-grabbing active:shadow-sm z-20 pointer-events-auto"
          :class="{ 'border-primary shadow-lg': activeHandle === 0 }"
          :style="{ left: handlePositions.start }"
        ></div>
        
        <!-- End Handle -->
        <div 
          class="absolute top-1/2 w-[18px] h-[18px] bg-popover border-2 border-primary/80 rounded-full transform -translate-x-1/2 -translate-y-1/2 cursor-grab shadow-md hover:shadow-lg active:cursor-grabbing active:shadow-sm z-20 pointer-events-auto"
          :class="{ 'border-primary shadow-lg': activeHandle === 1 }"
          :style="{ left: handlePositions.end }"
        ></div>
      </div>
    </div>

    <!-- Moving Tooltip (for drag operations) -->
    <Teleport to="body">
      <div
        v-show="tooltipVisible"
        ref="tooltipRef"
        class="fixed z-[9999] pointer-events-none bg-popover text-popover-foreground border border-border rounded-lg px-3 py-2 text-xs font-medium shadow-lg transform -translate-x-1/2 whitespace-nowrap transition-opacity duration-150"
        :style="{
          left: `${tooltipPosition.x}px`,
          top: `${tooltipPosition.y}px`,
        }"
      >
        {{ tooltipContent }}
      </div>
    </Teleport>

    <!-- Palette Button Tooltip -->
    <Teleport to="body">
      <div
        v-show="paletteButtonTooltipVisible"
        class="fixed z-[9999] pointer-events-none bg-primary text-background rounded-md px-3 py-2 text-xs font-medium shadow-xxl transform -translate-x-1/2 -translate-y-full transition-opacity duration-150 max-w-xs"
        :style="{
          left: `${paletteButtonTooltipPosition.x}px`,
          top: `${paletteButtonTooltipPosition.y}px`,
        }"
      >
        {{ currentPaletteDescription }}
      </div>
    </Teleport>


  </div>
</template> 

<style>


</style>